import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Compass,
  Search,
  BookmarkPlus,
  Check,
  LineChart as LineChartIcon,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorAlert } from '../components/ui/ErrorAlert';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import {
  saveKeyword,
  searchKeyword,
  type KeywordResearchResult,
  type KeywordRow,
} from '../api/keywordExplorer';

const REGIONS = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'AU', label: 'Australia' },
  { code: 'BR', label: 'Brazil' },
  { code: 'JP', label: 'Japan' },
];

type SortField = 'keyword' | 'relativeIndex' | 'monthlyVolume' | 'trendScorePct';
type SortDir = 'asc' | 'desc';

export function KeywordExplorerPage() {
  const { token } = useAuth();
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('US');
  const [filter, setFilter] = useState('');
  const [result, setResult] = useState<KeywordResearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartFor, setChartFor] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('relativeIndex');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await searchKeyword({ q: q.trim(), region }, token);
      setResult({
        seed: data.seed ?? q.trim(),
        region: data.region ?? region,
        seedMetrics: data.seedMetrics ?? {
          inTopTrends: false,
          rank: null,
          rankOutOf: null,
          pctGrowthWow: null,
          pctGrowthMom: null,
          pctGrowthYoy: null,
          relativeIndex: null,
          monthlyVolume: null,
          volumeSource: null,
        },
        timeSeries: Array.isArray(data.timeSeries) ? data.timeSeries : [],
        rows: Array.isArray(data.rows) ? data.rows : [],
        relatedKeywords: Array.isArray(data.relatedKeywords) ? data.relatedKeywords : [],
        topTrendingInRegion: Array.isArray(data.topTrendingInRegion)
          ? data.topTrendingInRegion
          : [],
        sources: Array.isArray(data.sources) ? data.sources : [],
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Search failed.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  // Pinterest publishes no per-keyword volume, so the backend only returns an absolute
  // number when it has been given a real anchor. Show that column only when it's real.
  const hasRealVolume = useMemo(
    () => (result?.rows ?? []).some((r) => r.monthlyVolume != null),
    [result],
  );
  // A search with no anchored volumes hides that column — don't leave the table sorted by
  // an all-null field the user can no longer see.
  const effectiveSortField =
    sortField === 'monthlyVolume' && !hasRealVolume ? 'relativeIndex' : sortField;

  const rows = useMemo(() => {
    if (!result) return [];
    const filterLower = filter.toLowerCase().trim();
    const filtered = filterLower
      ? result.rows.filter((r) => r.keyword.toLowerCase().includes(filterLower))
      : result.rows;
    const sign = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (effectiveSortField === 'keyword') {
        return sign * a.keyword.localeCompare(b.keyword);
      }
      const av = a[effectiveSortField];
      const bv = b[effectiveSortField];
      // == null so a backend that predates these fields (undefined) sorts like a null.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sign * (av - bv);
    });
  }, [result, filter, effectiveSortField, sortDir]);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'keyword' ? 'asc' : 'desc');
    }
  }

  return (
    <>
      <PageHeader
        icon={Compass}
        title="Keyword Explorer"
        description="Search a Pinterest keyword to see how popular it is relative to its neighbours, its trend %, and what's rising in the region."
      />

      <form
        onSubmit={onSubmit}
        className="mb-6 flex gap-2 rounded-lg border border-ink-200 bg-white p-2 shadow-sm"
      >
        <div className="flex flex-1 items-center gap-2 px-2">
          <Search className="h-5 w-5 text-ink-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='e.g. "minimalist wedding invitations"'
            className="w-full bg-transparent py-2 text-sm focus:outline-none"
          />
        </div>
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm text-ink-700 focus:border-brand-500 focus:outline-none"
        >
          {REGIONS.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={loading || !q.trim()}
          className="rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && <ErrorAlert message={error} />}

      {!result && !loading && !error && (
        <EmptyState
          icon={Compass}
          title="Enter a keyword to research"
          description="Trend growth is real data from Pinterest's Trends API. Popularity is a relative 0–100 index, not a search count — Pinterest doesn't publish per-keyword search volume."
        />
      )}

      {result && (
        <div className="rounded-lg border border-ink-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
            <div>
              <h2 className="text-sm font-semibold text-ink-900">
                {rows.length} keyword{rows.length === 1 ? '' : 's'}
              </h2>
              <p className="text-xs text-ink-500">
                Seed: <span className="font-medium text-brand-500">{result.seed}</span>
                {' · '}Region: <span className="font-medium">{result.region}</span>
                {result.sources.length > 0 && (
                  <>
                    {' · '}Sources:{' '}
                    <span className="font-medium">{result.sources.join(', ')}</span>
                  </>
                )}
              </p>
              <p className="mt-1 text-xs text-ink-400">
                Popularity is relative: 100 = the most popular term in this set. It is not a
                search count — Pinterest doesn't publish those.
              </p>
            </div>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by keyword…"
              className="w-56 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs focus:border-brand-500 focus:outline-none"
            />
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
                <th className="w-8 py-3"></th>
                <ThSortable
                  label="Keyword"
                  field="keyword"
                  currentField={effectiveSortField}
                  dir={sortDir}
                  onClick={toggleSort}
                  className="text-left"
                />
                <ThSortable
                  label="Popularity"
                  field="relativeIndex"
                  currentField={effectiveSortField}
                  dir={sortDir}
                  onClick={toggleSort}
                  className="text-right"
                  title="Relative to the most popular keyword in this result set (100 = top). Not a search count."
                />
                {hasRealVolume && (
                  <ThSortable
                    label="Volume"
                    field="monthlyVolume"
                    currentField={effectiveSortField}
                    dir={sortDir}
                    onClick={toggleSort}
                    className="text-right"
                    title="Absolute monthly searches, scaled to the real volume anchors configured on the server."
                  />
                )}
                <ThSortable
                  label="Trend"
                  field="trendScorePct"
                  currentField={effectiveSortField}
                  dir={sortDir}
                  onClick={toggleSort}
                  className="text-right"
                />
                <th className="w-24 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={hasRealVolume ? 6 : 5} className="py-12 text-center text-sm text-ink-500">
                    No keywords match this filter.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <RowItem
                    key={row.keyword}
                    row={row}
                    isSeed={row.keyword.toLowerCase() === result.seed.toLowerCase()}
                    showVolume={hasRealVolume}
                    token={token}
                    onOpenChart={() => setChartFor(row.keyword)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {chartFor && result && (
        <ChartDrawer
          keyword={chartFor}
          isSeed={chartFor.toLowerCase() === result.seed.toLowerCase()}
          result={result}
          onClose={() => setChartFor(null)}
        />
      )}
    </>
  );
}

function ThSortable({
  label,
  field,
  currentField,
  dir,
  onClick,
  className,
  title,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  dir: SortDir;
  onClick: (field: SortField) => void;
  className?: string;
  title?: string;
}) {
  const active = field === currentField;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`py-3 ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => onClick(field)}
        title={title}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-ink-900' : 'text-ink-500'
        } hover:text-ink-900`}
      >
        {label}
        <Icon className="h-3 w-3" />
      </button>
    </th>
  );
}

function RowItem({
  row,
  isSeed,
  showVolume,
  token,
  onOpenChart,
}: {
  row: KeywordRow;
  isSeed: boolean;
  showVolume: boolean;
  token: string | null;
  onOpenChart: () => void;
}) {
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);
  const mut = useMutation({
    mutationFn: () =>
      saveKeyword(
        { keyword: row.keyword, source: `keyword-explorer:${row.sourceAttribution}` },
        token,
      ),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['saved-keywords'] });
    },
  });

  const trend = row.trendScorePct;
  const rounded = trend === null ? null : Math.round(trend);

  return (
    <tr className={isSeed ? 'bg-brand-50/40' : 'hover:bg-ink-50'}>
      <td className="pl-6 pr-2 py-3">
        {isSeed && (
          <span className="inline-flex items-center rounded-full bg-brand-500 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
            seed
          </span>
        )}
      </td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || saved}
            className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-60"
            title={saved ? 'Saved' : 'Save keyword'}
          >
            {saved ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : (
              <BookmarkPlus className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={onOpenChart}
            className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            title="See trend chart"
          >
            <LineChartIcon className="h-4 w-4" />
          </button>
          <span className="truncate font-medium text-ink-900" title={row.keyword}>
            {row.keyword}
          </span>
        </div>
      </td>
      <td className="py-3 pr-4">
        {row.relativeIndex == null ? (
          <div className="text-right text-ink-400">—</div>
        ) : (
          <div
            className="flex items-center justify-end gap-2"
            title={`${formatIndex(row.relativeIndex)} / 100 relative to the top keyword in this set`}
          >
            <span
              aria-hidden="true"
              className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-ink-100"
            >
              <span
                className="block h-full rounded-full bg-brand-500"
                style={{ width: `${Math.max(2, Math.min(100, row.relativeIndex))}%` }}
              />
            </span>
            <span className="w-8 text-right tabular-nums text-ink-900">
              {formatIndex(row.relativeIndex)}
            </span>
          </div>
        )}
      </td>
      {showVolume && (
        <td className="py-3 pr-4 text-right tabular-nums text-ink-900">
          {row.monthlyVolume == null ? (
            <span className="text-ink-400">—</span>
          ) : (
            formatVolume(row.monthlyVolume)
          )}
        </td>
      )}
      <td className="py-3 pr-4 text-right">
        {rounded === null ? (
          <span className="text-ink-400">—</span>
        ) : (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              rounded >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-brand-50 text-brand-700'
            }`}
          >
            {rounded > 0 ? '+' : ''}
            {rounded}%
          </span>
        )}
      </td>
      <td className="pr-6 py-3"></td>
    </tr>
  );
}

function ChartDrawer({
  keyword,
  isSeed,
  result,
  onClose,
}: {
  keyword: string;
  isSeed: boolean;
  result: KeywordResearchResult;
  onClose: () => void;
}) {
  const data = isSeed
    ? result.timeSeries.map((p) => ({
        date: p.date,
        value: p.interest ?? p.score ?? 0,
      }))
    : [];
  const row = result.rows.find((r) => r.keyword.toLowerCase() === keyword.toLowerCase());
  const growth = row?.trendScorePct;
  const relativeIndex = row?.relativeIndex;
  const volume = row?.monthlyVolume;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 flex justify-end bg-ink-900/40"
      aria-modal="true"
      role="dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-lg overflow-y-auto bg-white p-6 shadow-lg"
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-400">Trend</p>
            <h2 className="mt-1 text-lg font-semibold text-ink-900">{keyword}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <dl className="grid grid-cols-2 gap-4 rounded-md border border-ink-100 bg-ink-50 p-4 text-sm">
          <div>
            <dt className="text-xs uppercase text-ink-400">Popularity</dt>
            <dd className="mt-1 font-semibold text-ink-900">
              {relativeIndex == null ? '—' : `${formatIndex(relativeIndex)} / 100`}
            </dd>
            <dd className="mt-0.5 text-xs text-ink-400">
              {volume == null
                ? 'relative to the top keyword in this set'
                : `≈ ${formatVolume(volume)} monthly searches`}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-ink-400">Trend %</dt>
            <dd className="mt-1 font-semibold text-ink-900">
              {growth === null || growth === undefined
                ? '—'
                : `${growth > 0 ? '+' : ''}${Math.round(growth)}%`}
            </dd>
          </div>
        </dl>

        <div className="mt-6">
          <h3 className="text-sm font-semibold text-ink-900">Interest over time</h3>
          <p className="mt-0.5 text-xs text-ink-500">
            Pinterest interest score in {result.region}
          </p>
          {data.length === 0 ? (
            <div className="mt-4 flex h-56 items-center justify-center rounded-md border border-dashed border-ink-200 text-sm text-ink-500">
              {isSeed
                ? "Pinterest didn't return time-series data for this seed."
                : 'Time-series is only available for the seed keyword.'}
            </div>
          ) : (
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="brandFillDrawer" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6d5efc" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#6d5efc" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#6d5efc"
                    strokeWidth={2}
                    fill="url(#brandFillDrawer)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** 0-100 index: whole numbers once it's meaningful, one decimal in the long tail. */
function formatIndex(v: number): string {
  return v >= 10 ? String(Math.round(v)) : v.toFixed(1);
}

function formatVolume(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`.replace('.0M', 'M');
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`.replace('.0K', 'K');
  return v.toLocaleString();
}
